import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  Users,
  TrendingUp,
  ArrowLeft,
  Loader2,
  Activity,
  Download,
  CheckCircle2,
  XCircle,
  LogOut,
  AlertTriangle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#D4AF37", "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6"];

function distToChartData(obj) {
  return Object.entries(obj || {}).map(([name, value]) => ({ name, value }));
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = sessionStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/export`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `shaft_quiz_sessions_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Export error:", err);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("admin_token");
      if (!token) {
        navigate("/admin", { replace: true });
        return;
      }
      try {
        const authHeaders = { Authorization: `Bearer ${token}` };
        const [statsRes, sessionsRes, errorsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: authHeaders }),
          axios.get(`${API}/admin/sessions?limit=20`, { headers: authHeaders }),
          axios.get(`${API}/admin/errors?limit=50`, { headers: authHeaders }),
        ]);
        setStats(statsRes.data);
        setSessions(sessionsRes.data.sessions || []);
        setErrors(errorsRes.data.errors || []);
      } catch (err) {
        if (err.response?.status === 401) {
          sessionStorage.removeItem("admin_token");
          navigate("/admin", { replace: true });
          return;
        }
        if (process.env.NODE_ENV === "development") console.error("Admin fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#03170D] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const clubData = distToChartData(stats?.club_type_distribution);
  const speedData = distToChartData(stats?.swing_speed_distribution);
  const budgetData = distToChartData(stats?.budget_distribution);
  const skillData = distToChartData(stats?.skill_level_distribution);
  const topShafts = stats?.top_recommended_shafts || [];

  return (
    <div className="min-h-screen bg-[#03170D] p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              data-testid="admin-back-btn"
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-[#A3B8AA] hover:text-white mb-2 -ml-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to App
            </Button>
            <h1 data-testid="admin-title" className="text-3xl sm:text-4xl tracking-tight font-medium text-white">
              Analytics Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="export-csv-btn"
              onClick={handleExportCSV}
              disabled={exporting}
              className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold rounded-full px-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exporting ? "Exporting..." : "Download CSV"}
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              onClick={() => {
                sessionStorage.removeItem("admin_token");
                navigate("/admin", { replace: true });
              }}
              className="border-[#1A4026] text-[#A3B8AA] hover:text-white hover:border-red-500/50 bg-transparent rounded-full"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-[#0A2716] border-[#1A4026]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#647D6C] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <Users className="h-4 w-4" /> Total Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="total-sessions" className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.total_sessions || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A2716] border-[#1A4026]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#647D6C] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <CheckCircle2 className="h-4 w-4" /> Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="completed-sessions" className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.completed_sessions || 0}
              </p>
              <p className="text-xs text-[#647D6C] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.completion_rate || 0}% completion rate
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A2716] border-[#1A4026]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#647D6C] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <XCircle className="h-4 w-4" /> Partial / Dropped
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="partial-sessions" className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.partial_sessions || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A2716] border-[#1A4026]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#647D6C] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                <TrendingUp className="h-4 w-4" /> Shop Now Clicks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="shop-clicks" className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.action_stats?.shop_now_click || 0}
              </p>
            </CardContent>
          </Card>
          <Card className={`border-[#1A4026] ${stats?.unresolved_errors > 0 ? "bg-[#2A1610] border-red-900/50" : "bg-[#0A2716]"}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm flex items-center gap-2 ${stats?.unresolved_errors > 0 ? "text-red-400" : "text-[#647D6C]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                <AlertTriangle className="h-4 w-4" /> Errors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="error-count" className={`text-3xl font-bold ${stats?.unresolved_errors > 0 ? "text-red-400" : "text-white"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.unresolved_errors || 0}
              </p>
              <p className="text-xs text-[#647D6C] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                {stats?.total_errors || 0} total
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <Tabs defaultValue="overview" className="mb-8">
          <TabsList className="bg-[#0A2716] border border-[#1A4026]">
            <TabsTrigger
              data-testid="tab-overview"
              value="overview"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#03170D]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-sessions"
              value="sessions"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#03170D]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Recent Sessions
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-shafts"
              value="shafts"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#03170D]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Top Shafts
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-errors"
              value="errors"
              className={`data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#03170D] ${stats?.unresolved_errors > 0 ? "text-red-400" : ""}`}
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Errors {stats?.unresolved_errors > 0 ? `(${stats.unresolved_errors})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Club Type Distribution */}
              <Card className="bg-[#0A2716] border-[#1A4026]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Club Type Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {clubData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={clubData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1A4026" />
                        <XAxis dataKey="name" tick={{ fill: "#647D6C", fontSize: 12 }} />
                        <YAxis tick={{ fill: "#647D6C", fontSize: 12 }} />
                        <RTooltip
                          contentStyle={{ background: "#0A2716", border: "1px solid #1A4026", color: "#fff" }}
                        />
                        <Bar dataKey="value" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-[#647D6C] text-center py-8" style={{ fontFamily: "Outfit, sans-serif" }}>No data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Swing Speed Distribution */}
              <Card className="bg-[#0A2716] border-[#1A4026]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Swing Speed Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {speedData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={speedData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name }) => name}
                        >
                          {speedData.map((entry, i) => (
                            <Cell key={entry.name || `speed-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <RTooltip
                          contentStyle={{ background: "#0A2716", border: "1px solid #1A4026", color: "#fff" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-[#647D6C] text-center py-8" style={{ fontFamily: "Outfit, sans-serif" }}>No data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Budget Distribution */}
              <Card className="bg-[#0A2716] border-[#1A4026]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Budget Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {budgetData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={budgetData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1A4026" />
                        <XAxis dataKey="name" tick={{ fill: "#647D6C", fontSize: 12 }} />
                        <YAxis tick={{ fill: "#647D6C", fontSize: 12 }} />
                        <RTooltip
                          contentStyle={{ background: "#0A2716", border: "1px solid #1A4026", color: "#fff" }}
                        />
                        <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-[#647D6C] text-center py-8" style={{ fontFamily: "Outfit, sans-serif" }}>No data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Skill Level */}
              <Card className="bg-[#0A2716] border-[#1A4026]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Golfer Journey Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {skillData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={skillData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name }) => name}
                        >
                          {skillData.map((entry, i) => (
                            <Cell key={entry.name || `skill-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <RTooltip
                          contentStyle={{ background: "#0A2716", border: "1px solid #1A4026", color: "#fff" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-[#647D6C] text-center py-8" style={{ fontFamily: "Outfit, sans-serif" }}>No data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            <div className="mt-6 space-y-3">
              {sessions.length > 0 ? (
                sessions.map((s, i) => (
                  <motion.div
                    key={s.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-[#0A2716] border border-[#1A4026] rounded-xl p-4"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={`border-none ${s.quiz_completed ? "bg-[rgba(16,185,129,0.15)] text-[#10B981]" : "bg-[rgba(239,68,68,0.15)] text-[#EF4444]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.quiz_completed ? "Completed" : `Dropped at Step ${s.last_step || "?"}`}
                      </Badge>
                      <Badge className="bg-[rgba(212,175,55,0.15)] text-[#D4AF37] border-none" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.answers?.club_type || "N/A"}
                      </Badge>
                      <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.answers?.swing_speed || "N/A"}
                      </Badge>
                      <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.answers?.budget || "N/A"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.recommendations?.length || 0} recommendations &middot; {s.products_found} products found
                      </p>
                      <p className="text-xs text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.timestamp ? new Date(s.timestamp).toLocaleDateString() : ""}
                      </p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <p className="text-[#647D6C] text-center py-8" style={{ fontFamily: "Outfit, sans-serif" }}>No sessions yet</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="shafts">
            <div className="mt-6 space-y-3">
              {topShafts.length > 0 ? (
                topShafts.map((shaft, i) => (
                  <div key={shaft.name || i} className="bg-[#0A2716] border border-[#1A4026] rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className="w-8 h-8 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-sm font-semibold text-[#D4AF37]"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {i + 1}
                      </span>
                      <p className="text-white font-medium truncate max-w-xs" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {shaft.name}
                      </p>
                    </div>
                    <Badge className="bg-[rgba(212,175,55,0.15)] text-[#D4AF37] border-none" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {shaft.count} recommendation{shaft.count !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-[#647D6C] text-center py-8" style={{ fontFamily: "Outfit, sans-serif" }}>No data yet</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="errors">
            <div className="mt-6">
              {errors.length > 0 && (
                <div className="flex justify-end mb-4">
                  <Button
                    data-testid="resolve-all-errors-btn"
                    variant="outline"
                    onClick={async () => {
                      const token = sessionStorage.getItem("admin_token");
                      try {
                        await axios.post(`${API}/admin/errors/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        setErrors((prev) => prev.map((e) => ({ ...e, resolved: true })));
                        setStats((prev) => prev ? { ...prev, unresolved_errors: 0 } : prev);
                      } catch (err) { if (process.env.NODE_ENV === "development") console.error(err); }
                    }}
                    className="border-[#1A4026] text-[#A3B8AA] hover:text-white hover:border-[#D4AF37] bg-transparent rounded-full text-sm"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Mark All Resolved
                  </Button>
                </div>
              )}
              <div className="space-y-3">
                {errors.length > 0 ? (
                  errors.map((err, i) => (
                    <motion.div
                      key={err.timestamp || i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className={`rounded-xl p-4 border ${err.resolved ? "bg-[#0A2716] border-[#1A4026] opacity-60" : "bg-[#2A1610] border-red-900/40"}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge className={`border-none ${err.resolved ? "bg-[rgba(16,185,129,0.15)] text-[#10B981]" : "bg-[rgba(239,68,68,0.15)] text-[#EF4444]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                              {err.resolved ? "Resolved" : err.type?.replace("_", " ") || "Error"}
                            </Badge>
                            {err.context?.club_type && (
                              <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {err.context.club_type}
                              </Badge>
                            )}
                            {err.context?.customer_email && (
                              <Badge variant="outline" className="border-[#1A4026] text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {err.context.customer_email}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-[#A3B8AA] break-words" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {err.detail}
                          </p>
                        </div>
                        <p className="text-xs text-[#647D6C] flex-shrink-0" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {err.timestamp ? new Date(err.timestamp).toLocaleString() : ""}
                        </p>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-8 w-8 text-[#10B981] mx-auto mb-3" />
                    <p className="text-[#647D6C]" style={{ fontFamily: "Outfit, sans-serif" }}>No errors &mdash; everything&#39;s running smoothly</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
